import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, MessageSquare, RefreshCw, ChevronRight, ChevronDown, Search,
  AlertTriangle, Calendar
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import { formatTime, formatDateHeader } from '../../utils/format';
import { etDateKey } from '../../utils/etDate';
import { pnlColor as pnlColorCls } from '../../utils/pnl';
import { summarizeDecisions } from '../../utils/threads';
import { ForumPost, ThreadMessage, ThreadRecommendation, ThreadDecision } from '../../types';

interface ChatPageProps {
  onBack: () => void;
}

// B-026: threads group under their ET day (UTC put a 9 PM ET thread under tomorrow)
const getDateKey = etDateKey;

const getThreadSubject = (thread: ForumPost): string => {
  if (thread.post_type === 'exit_discussion') {
    let syms = thread.symbols || [];
    // Fall back to symbols from positions or decisions if top-level is empty
    if (syms.length === 0 && thread.messages) {
      const fromDecisions = new Set<string>();
      for (const m of thread.messages) {
        for (const d of m.decisions || []) {
          if (d.symbol) fromDecisions.add(d.symbol);
        }
      }
      syms = Array.from(fromDecisions);
    }
    const symStr = syms.slice(0, 6).map(s => '$' + s).join(' ');
    return 'Exit Review: ' + (symStr || 'Positions');
  }
  const syms = (thread.symbols || []).map(s => '$' + s).join(' ');
  return syms || 'Market Analysis';
};

const ChatPage: React.FC<ChatPageProps> = ({ onBack }) => {
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchSymbol, setSearchSymbol] = useState('');

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const symbol = searchSymbol.trim() || undefined;
      const data = await apiService.getForumFeed(100, symbol);
      setPosts(data?.posts || []);
    } catch (err) {
      console.error('Failed to fetch forum:', err);
    } finally {
      setLoading(false);
    }
  }, [searchSymbol]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const threads = useMemo(() =>
    posts.filter(p => p.post_type === 'cycle_discussion' || p.post_type === 'exit_discussion'),
    [posts]
  );

  const groupedThreads = useMemo(() => {
    const groups: { dateKey: string; label: string; threads: ForumPost[] }[] = [];
    const map = new Map<string, ForumPost[]>();
    for (const t of threads) {
      const key = getDateKey(t.created_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    const sortedKeys = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));
    for (const key of sortedKeys) {
      const items = map.get(key)!;
      groups.push({ dateKey: key, label: formatDateHeader(items[0].created_at), threads: items });
    }
    return groups;
  }, [threads]);

  return (
    <div className="min-h-[100dvh] bg-slate-950">
      <div className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div className="flex-1">
              <h1 className="text-base font-bold text-white">Agent Trading Desk</h1>
              <p className="text-11 text-slate-500">{threads.length} discussions</p>
            </div>
            <div className="flex items-center gap-2">
              <form onSubmit={(e) => { e.preventDefault(); fetchPosts(); }} className="flex items-center">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchSymbol}
                    onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
                    placeholder="Filter symbol..."
                    className="pl-8 pr-3 py-1.5 w-32 text-xs bg-slate-900 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50"
                  />
                </div>
              </form>
              <button onClick={fetchPosts} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-600" /></div>
        ) : threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <MessageSquare className="w-12 h-12 text-slate-700 mb-3" />
            <p className="text-sm text-slate-500">{searchSymbol ? 'No discussions about $' + searchSymbol : 'No discussions yet'}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedThreads.map((group) => (
              <div key={group.dateKey}>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <Calendar className="w-3.5 h-3.5 text-slate-600" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{group.label}</span>
                  <div className="flex-1 h-px bg-slate-800/60" />
                  <span className="text-10 text-slate-600">{group.threads.length} thread{group.threads.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {group.threads.map((post) => <ThreadRow key={post.id} thread={post} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Thread Row
// ============================================================
const ThreadRow: React.FC<{ thread: ForumPost }> = ({ thread }) => {
  const [expanded, setExpanded] = useState(false);
  const messages = thread.messages || [];
  const recs = thread.recommendations || [];
  const consensus = thread.consensus || {};
  const isExit = thread.post_type === 'exit_discussion';
  const subject = getThreadSubject(thread);

  // 1.10: the full decision taxonomy is ALWAYS computable from the stored per-decision
  // records — old and new threads alike. Outcome counts (post-gate book truth) come from
  // the thread fields, present on live-1.10 threads and backfilled historical ones.
  const tax = summarizeDecisions(messages);
  const executedCloses = thread.executed_closes;
  const vetoedCloses = thread.vetoed_closes || 0;
  const openedCount = thread.total_opened;
  const gatedCount = thread.total_gated || 0;

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${
      isExit ? 'border-amber-500/15 bg-slate-900/80' : 'border-slate-800 bg-slate-900/60'
    } ${expanded ? 'ring-1 ' + (isExit ? 'ring-amber-500/20' : 'ring-purple-500/15') : ''}`}>
      <div
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
          isExit ? 'hover:bg-amber-500/5' : 'hover:bg-slate-800/50'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          isExit ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-purple-500/10 border border-purple-500/20'
        }`}>
          {isExit
            ? <AlertTriangle className="w-4 h-4 text-amber-400" />
            : <MessageSquare className="w-4 h-4 text-purple-400" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white truncate">{subject}</span>
            {isExit && (
              <span className="text-10 md:text-9 px-1.5 py-0.5 rounded-md font-bold shrink-0 bg-amber-500/15 text-amber-400 border border-amber-500/20">EXIT</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-11 text-slate-500">{thread.agent_count || messages.length} agents</span>
            <span className="text-11 text-slate-700">&middot;</span>
            {isExit ? (
              <>
                <span className="text-11 text-red-400/80 font-mono">
                  {tax.close} CLOSE{executedCloses !== undefined ? ` (${executedCloses} executed${vetoedCloses > 0 ? `, ${vetoedCloses} vetoed` : ''})` : ''}
                </span>
                <span className="text-11 text-green-400/80 font-mono">{tax.hold} HOLD</span>
              </>
            ) : (
              <>
                <span className="text-11 text-slate-500 font-mono">{tax.total} decisions:</span>
                <span className="text-11 text-green-400/80 font-mono">{tax.take} TAKE{tax.reduce > 0 ? ` · ${tax.reduce} REDUCE` : ''}</span>
                <span className="text-11 text-slate-400/80 font-mono">{tax.skip} SKIP</span>
                {openedCount !== undefined && (
                  <span className="text-11 text-cyan-400/80 font-mono">→ {openedCount} opened{gatedCount > 0 ? ` · ${gatedCount} gated` : ''}</span>
                )}
              </>
            )}
            <span className="text-11 text-slate-600 ml-auto shrink-0">{formatTime(thread.created_at)}</span>
          </div>
        </div>
        {expanded
          ? <ChevronDown className="w-4 h-4 text-slate-600 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
        }
      </div>

      {expanded && (
        <div className="border-t border-slate-800/60">
          {/* Rec Cards for entry threads */}
          {!isExit && recs.length > 0 && (
            <div className="px-4 py-3 border-b border-slate-800/40 bg-slate-950/40">
              <p className="text-10 text-slate-500 uppercase tracking-wider mb-2.5 font-semibold">Candidates Under Review</p>
              <div className="flex gap-2.5 overflow-x-auto pb-1">
                {recs.map((rec, i) => <RecCard key={i} rec={rec} consensus={consensus[rec.symbol]} />)}
              </div>
            </div>
          )}

          {/* Position cards for exit threads */}
          {isExit && thread.positions && thread.positions.length > 0 && (
            <div className="px-4 py-3 border-b border-slate-800/40 bg-slate-950/40">
              <p className="text-10 text-slate-500 uppercase tracking-wider mb-2.5 font-semibold">Positions Under Review</p>
              <div className="flex gap-2.5 overflow-x-auto pb-1">
                {thread.positions.map((pos, i) => {
                  const pnl = pos.pnl_pct ?? 0;
                  const pnlColor = pnlColorCls(pnl);
                  const ta = pos.current_ta_snapshot || {};
                  const weeklyColor = ta.weekly_trend === 'bullish' ? 'text-green-400' : ta.weekly_trend === 'bearish' ? 'text-red-400' : 'text-slate-300';
                  const rsiColor = (ta.daily_rsi ?? 50) <= 30 ? 'text-green-400' : (ta.daily_rsi ?? 50) >= 70 ? 'text-red-400' : 'text-slate-300';
                  return (
                    <div key={i} className="shrink-0 px-3.5 py-3 bg-slate-900 border border-slate-800 rounded-xl min-w-[250px] max-w-[280px]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white font-mono">${pos.symbol}</span>
                          <span className="text-10 md:text-9 px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 rounded-md font-medium">{pos.strategy === 'Cash-Secured Put' ? 'CSP' : pos.strategy === 'Covered Call' ? 'CC' : pos.strategy}</span>
                        </div>
                        <span className={`text-10 font-mono font-semibold ${pnlColor}`}>{pnl >= 0 ? '+' : ''}{pnl.toFixed(1)}%</span>
                      </div>
                      {/* mobile-ok: three $-value pairs, short numerics */}
                      <div className="grid grid-cols-3 gap-x-2 gap-y-1.5 text-11 mb-2.5">
                        <div><span className="text-slate-500 block text-10">Strike</span><span className="text-white font-mono font-semibold">${pos.strike?.toFixed(0)}</span></div>
                        <div><span className="text-slate-500 block text-10">Entry</span><span className="text-white font-mono font-semibold">${pos.entry_price?.toFixed(2)}</span></div>
                        <div><span className="text-slate-500 block text-10">Current</span><span className="text-white font-mono font-semibold">${pos.current_price?.toFixed(2)}</span></div>
                      </div>
                      {(ta.daily_rsi != null || ta.weekly_trend || ta.support_level != null) && (
                        <div className="border-t border-slate-800/60 pt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-11">
                          {ta.daily_rsi != null && <div className="flex justify-between"><span className="text-slate-500">RSI</span><span className={`font-mono ${rsiColor}`}>{ta.daily_rsi?.toFixed(1)}</span></div>}
                          {ta.weekly_trend && <div className="flex justify-between"><span className="text-slate-500">Weekly</span><span className={`font-mono ${weeklyColor}`}>{ta.weekly_trend}</span></div>}
                          {ta.support_level != null && <div className="flex justify-between"><span className="text-slate-500">Support</span><span className="text-white font-mono">${ta.support_level?.toFixed(0)}</span></div>}
                          {ta.sma_50 != null && <div className="flex justify-between"><span className="text-slate-500">SMA50</span><span className="text-white font-mono">${ta.sma_50?.toFixed(0)}</span></div>}
                        </div>
                      )}
                      <div className="border-t border-slate-800/60 mt-2 pt-2 text-10 text-slate-500 leading-relaxed">{pos.flag_reason}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Agent messages */}
          <div className="divide-y divide-slate-800/40">
            {messages.map((msg) => <AgentMessage key={msg.order} msg={msg} isExit={isExit} />)}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Recommendation Card
// ============================================================
const RecCard: React.FC<{
  rec: ThreadRecommendation;
  consensus?: { takes: number; skips: number; consensus: string };
}> = ({ rec, consensus }) => {
  const consensusColor = consensus?.consensus === 'bullish' ? 'text-green-400' :
    consensus?.consensus === 'bearish' ? 'text-red-400' : 'text-yellow-400';
  const rsiColor = (rec.rsi ?? 50) <= 30 ? 'text-green-400' : (rec.rsi ?? 50) >= 70 ? 'text-red-400' : 'text-slate-300';
  const macdColor = rec.macd_signal === 'bullish' ? 'text-green-400' : rec.macd_signal === 'bearish' ? 'text-red-400' : 'text-slate-300';

  return (
    <div className="shrink-0 px-3.5 py-3 bg-slate-900 border border-slate-800 rounded-xl min-w-[250px] max-w-[280px]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-white font-mono">${rec.symbol}</span>
          <span className="text-10 md:text-9 px-1.5 py-0.5 bg-cyan-500/10 text-cyan-400 rounded-md font-medium">{rec.strategy}</span>
        </div>
        <span className="text-10 text-slate-500 font-mono">{rec.dte}d</span>
      </div>
      {/* mobile-ok: three $-value pairs, short numerics */}
                      <div className="grid grid-cols-3 gap-x-2 gap-y-1.5 text-11 mb-2.5">
        <div><span className="text-slate-500 block text-10">Strike</span><span className="text-white font-mono font-semibold">${rec.strike?.toFixed(0)}</span></div>
        <div><span className="text-slate-500 block text-10">Price</span><span className="text-white font-mono font-semibold">${rec.stock_price?.toFixed(1)}</span></div>
        <div><span className="text-slate-500 block text-10">Premium</span><span className="text-green-400 font-mono font-semibold">{rec.premium_pct?.toFixed(2)}%</span></div>
      </div>
      <div className="border-t border-slate-800/60 pt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-11">
        <div className="flex justify-between"><span className="text-slate-500">RSI</span><span className={`font-mono ${rsiColor}`}>{rec.rsi?.toFixed(1) ?? '\u2014'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">MACD</span><span className={`font-mono ${macdColor}`}>{rec.macd_signal ?? '\u2014'}</span></div>
        {rec.support_level != null && <div className="flex justify-between"><span className="text-slate-500">Support</span><span className="text-white font-mono">${rec.support_level.toFixed(0)}</span></div>}
        {rec.resistance_level != null && <div className="flex justify-between"><span className="text-slate-500">Resist</span><span className="text-white font-mono">${rec.resistance_level.toFixed(0)}</span></div>}
      </div>
      {rec.confluence_score != null && (
        <div className="border-t border-slate-800/60 mt-2 pt-2 flex items-center justify-between text-11">
          <span className="text-slate-500">Confluence</span>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${(rec.confluence_score || 0) >= 60 ? 'bg-green-500' : (rec.confluence_score || 0) >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${Math.min(rec.confluence_score || 0, 100)}%` }} />
            </div>
            <span className="text-slate-300 font-mono">{rec.confluence_score?.toFixed(0)}%</span>
          </div>
        </div>
      )}
      {rec.timeframe_signals && (
        <div className="border-t border-slate-800/60 mt-2 pt-2 flex items-center gap-1">
          {(['weekly', 'daily', '4h', '1h'] as const).map(tf => {
            const sig = (rec.timeframe_signals as Record<string, string>)?.[tf];
            if (!sig || sig === 'N/A') return null;
            const c = sig === 'bullish' ? 'bg-green-500/15 text-green-400 border-green-500/20' : sig === 'bearish' ? 'bg-red-500/15 text-red-400 border-red-500/20' : 'bg-slate-800 text-slate-400 border-slate-700';
            return <span key={tf} className={`text-10 md:text-9 px-1.5 py-0.5 rounded-md font-mono border ${c}`}>{tf.toUpperCase()}{sig === 'bullish' ? '\u2191' : sig === 'bearish' ? '\u2193' : '\u2014'}</span>;
          })}
        </div>
      )}
      {consensus && (
        <div className={`mt-2 pt-2 border-t border-slate-800/60 text-11 font-medium ${consensusColor}`}>
          {consensus.takes}{'\u2191'} {consensus.skips}{'\u2193'} {'\u2014'} {consensus.consensus}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Agent Message
// ============================================================
const AgentMessage: React.FC<{ msg: ThreadMessage; isExit?: boolean }> = ({ msg, isExit }) => {
  return (
    <div className="flex gap-3 px-4 py-3.5">
      <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center text-lg border border-slate-700/50 shrink-0">
        {msg.agent_emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs font-bold text-white">{msg.agent_name}</span>
          <div className="flex items-center gap-1 flex-wrap">
            {msg.decisions.map((d, i) => <DecisionBadge key={i} decision={d} />)}
          </div>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-line">{msg.commentary}</p>
        {msg.decisions.length > 0 && (
          <div className="mt-2.5 space-y-1.5">
            {msg.decisions.map((d, i) => <DecisionDetail key={i} decision={d} />)}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Decision Badge
// ============================================================
const DecisionBadge: React.FC<{ decision: ThreadDecision }> = ({ decision }) => {
  const config: Record<string, { bg: string; text: string; border: string }> = {
    TAKE: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
    SKIP: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20' },
    REDUCE: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20' },
    CLOSE: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
    HOLD: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20' },
  };
  const c = config[decision.decision] || config.SKIP;
  // 1.10: outcome badge — a decision that a deterministic gate stopped must never read
  // as executed (the chats-vs-book number disparity of 2026-07-08).
  const outcomeBadge =
    decision.outcome === 'vetoed' ? { label: 'VETOED', cls: 'bg-red-500/20 text-red-300 border-red-500/40' } :
    decision.outcome === 'gated' ? { label: 'GATED', cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' } :
    decision.outcome === 'reverted' ? { label: 'KEPT SHARES', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' } :
    decision.outcome === 'failed' || (decision.outcome || '').startsWith('refused') ||
    (decision.outcome || '').startsWith('error') || (decision.outcome || '').startsWith('not_executed') ?
      { label: 'NOT EXECUTED', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' } :
    null;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-10 font-semibold ${c.bg} ${c.text} ${c.border}`}>
      {decision.decision} {decision.symbol}
      {outcomeBadge && (
        <span className={`ml-1 px-1 py-px rounded border text-10 md:text-9 font-bold ${outcomeBadge.cls}`} title={decision.veto_reason || decision.gate || ''}>
          {outcomeBadge.label}
        </span>
      )}
    </span>
  );
};

// ============================================================
// Decision Detail
// ============================================================
const DecisionDetail: React.FC<{ decision: ThreadDecision }> = ({ decision }) => {
  const color = decision.decision === 'TAKE' ? 'border-green-500/25 bg-green-500/5' :
    decision.decision === 'CLOSE' ? 'border-red-500/25 bg-red-500/5' :
    decision.decision === 'HOLD' || decision.decision === 'REDUCE' ? 'border-yellow-500/25 bg-yellow-500/5' :
    'border-slate-500/25 bg-slate-500/5';

  return (
    <div className={`px-3 py-2 border-l-2 rounded-r-lg text-xs ${color}`}>
      <span className="text-slate-400 font-medium">{decision.symbol}:</span>{' '}
      <span className="text-slate-300">{decision.reasoning}</span>
    </div>
  );
};

export default ChatPage;
